import { lazy, Suspense, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { trackPageView } from "./lib/analytics";
import { trackPostHogPageView } from "./lib/posthog";

// Sends a GA4 page_view and PostHog $pageview on every client-side route change
const RouteChangeTracker = () => {
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname + location.search;
    trackPageView(path);
    trackPostHogPageView(path);
  }, [location.pathname, location.search]);
  return null;
};
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import AnalyticsInstrumentation from "./components/AnalyticsInstrumentation";

// Homepage is eagerly loaded — it's the primary entry point
import Index from "./pages/Index";

// All other routes are lazy-loaded to reduce initial bundle
const Contact = lazy(() => import("./pages/Contact"));
const Company = lazy(() => import("./pages/Company"));
const Work = lazy(() => import("./pages/Work"));
const Solutions = lazy(() => import("./pages/Solutions"));
const Vixi = lazy(() => import("./pages/Vixi"));
const Capture = lazy(() => import("./pages/vixi/Capture"));
const Magic = lazy(() => import("./pages/vixi/Magic"));
const Streaming = lazy(() => import("./pages/vixi/Streaming"));
const LightShow = lazy(() => import("./pages/vixi/LightShow"));
const AddOns = lazy(() => import("./pages/vixi/AddOns"));
const Licensing = lazy(() => import("./pages/vixi/Licensing"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const WPPage = lazy(() => import("./pages/WPPage"));
const AdminGate = lazy(() => import("./components/AdminGate"));
const ContentGuide = lazy(() => import("./pages/ContentGuide"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
import ConsentManager from "./components/consent/ConsentManager";


const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <RouteChangeTracker />
        <AnalyticsInstrumentation />
        <Suspense fallback={null}>
          <Routes>
            
            <Route path="/" element={<Index />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/company" element={<Company />} />
            <Route path="/work" element={<Work />} />
            <Route path="/solutions" element={<Solutions />} />
            <Route path="/vixi" element={<Vixi />} />
            <Route path="/vixi/capture" element={<Capture />} />
            <Route path="/vixi/magic" element={<Magic />} />
            <Route path="/vixi/streaming" element={<Streaming />} />
            <Route path="/vixi/lightshow" element={<LightShow />} />
            <Route path="/vixi/add-ons" element={<AddOns />} />
            <Route path="/vixi/licensing" element={<Licensing />} />
            <Route path="/privacy" element={<LegalPage path="/privacy" />} />
            <Route path="/cookies" element={<LegalPage path="/cookies" />} />
            <Route path="/terms" element={<LegalPage path="/terms" />} />
            {/* Legacy path redirects */}
            <Route path="/about" element={<Navigate to="/company" replace />} />
            <Route path="/disciplines" element={<Navigate to="/solutions" replace />} />
            <Route path="/tech" element={<Navigate to="/solutions" replace />} />
            <Route path="/contact-us" element={<Navigate to="/contact" replace />} />
            <Route path="/projects/:rest" element={<Navigate to="/work" replace />} />
            <Route path="/projects" element={<Navigate to="/work" replace />} />
            <Route path="/blog/:slug" element={<Navigate to="/" replace />} />
            <Route path="/blog" element={<Navigate to="/" replace />} />
            <Route path="/hello-world" element={<Navigate to="/" replace />} />
            <Route path="/sample-page" element={<Navigate to="/" replace />} />
            <Route path="/feed" element={<Navigate to="/" replace />} />
            <Route path="/content-guide" element={<Suspense fallback={null}><AdminGate><ContentGuide /></AdminGate></Suspense>} />
            <Route path="*" element={<WPPage />} />
          </Routes>
        </Suspense>
        <ConsentManager />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
